import { FC, useCallback } from 'react';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button, { isButtonDisabled } from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import useConfirm from 'src/hooks/useConfirm';
import { useCancelInvoicesMutation } from 'src/services/invoices';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from './InvoiceDetailsContext';
import { CancelSelectedDocumentInternalContent } from './ModalInvoiceDialogs';
import useInvoiceButtonState from './useInvoiceButtonState';

interface CancelInvoiceProps {}

const CancelInvoice: FC<CancelInvoiceProps> = () => {
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);
  const title = useContextSelector(InvoiceContext, (value) => value.title);
  const refreshPage = useContextSelector(InvoiceContext, (value) => value.refreshPage);

  const { confirm } = useConfirm();
  const { mutateAsync: cancelInvoices, isPending: cancelInvoicesLoading } =
    useCancelInvoicesMutation();
  const [messages, buttonState] = useInvoiceButtonState('Cancel');

  const handleCancelInvoice = useCallback(() => {
    const { receivingReceiptId, receiptId, poId } = invoice;
    const invoiceIds: number[] = [receiptId];
    confirm({
      title: 'Cancel Selected Documents',
      content: (
        <CancelSelectedDocumentInternalContent
          linkedDocumentId={receivingReceiptId ? String(receivingReceiptId) : null}
          documentType="Invoice"
          poId={String(poId)}
        />
      ),
      cancelButtonText: 'Cancel',
      cancelColor: 'info',
      confirmButtonText: 'OK',
    })
      .then(async (cancelApproved) => {
        if (cancelApproved) {
          await cancelInvoices(invoiceIds);
          refreshPage();
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }, [cancelInvoices, confirm, invoice, refreshPage]);

  return (
    <PopUp
      popupAlign={{ horizontal: 'center', vertical: 'top' }}
      content={
        <div className="m-1 max-w-[350px]">
          <PopUpContentWrapper type="Error" contents={[...messages]} />
        </div>
      }
      show={isButtonDisabled(buttonState)}
    >
      <Button
        onClick={handleCancelInvoice}
        color="error"
        buttonState={buttonState}
        disabled={isButtonDisabled(buttonState)}
        loading={cancelInvoicesLoading}
      >
        Cancel {title}
      </Button>
    </PopUp>
  );
};

export default CancelInvoice;
